import { useMutation, useQueryClient } from '@tanstack/react-query';
import { captureBillingError } from 'services/sentry/captureBillingError';
import {
  SUBSCRIPTION_STATE,
  type GetBillingInfoResponse,
} from '../httpClients/BillingClient/api.schema';
import { BillingClient } from '../httpClients/BillingClient/BillingClient';
import { QUERY_KEYS } from '../queries/consts';
import { MUTATION_KEYS } from './consts';

/**
 * The two change-plan directions are mutually exclusive, modelled as a
 * discriminated union so an invalid combination (upgrade with an `effectiveAt`,
 * or downgrade without one) is unrepresentable:
 * - Upgrade: an in-place proration charged immediately. The FE can't synthesize
 *   the new active plan's price/name, so the optimistic write is skipped and the
 *   authoritative state comes from the onSettled refetch — no `effectiveAt`.
 * - Downgrade: scheduled at period end, so `effectiveAt` is required to drive the
 *   optimistic scheduled-change note.
 */
export type ChangePlanVariables =
  | { targetProductId: string; isUpgrade: true; effectiveAt?: never }
  | { targetProductId: string; isUpgrade?: false; effectiveAt: string };

/**
 * Hook for a self-serve plan change in either direction. A downgrade schedules a
 * smaller credit pack at period end: the user keeps their pool until renewal, and
 * on success the cached subscriptionState flips to scheduledToChange with the
 * target/effective date so the UI row updates without a refetch. An upgrade
 * (`isUpgrade: true`) applies immediately with a prorated charge and has no
 * optimistic write — its authoritative state comes from the onSettled refetch,
 * which also invalidates the quota balance since the larger pack's credits land
 * right away. On settle both directions invalidate billing-info and account-info.
 * @returns The react-query useMutation object; call `mutate`/`mutateAsync` with a
 *   downgrade (`effectiveAt`) or upgrade (`isUpgrade: true`) variables shape.
 */
export const useChangePlanMutation = () => {
  const queryClient = useQueryClient();
  const billingInfoKey = [QUERY_KEYS.BILLING_INFO];

  return useMutation({
    mutationKey: [MUTATION_KEYS.CHANGE_PLAN],
    mutationFn: ({ targetProductId }: ChangePlanVariables) =>
      BillingClient.changePlan(targetProductId),
    onSuccess: (_data, variables) => {
      // An immediate upgrade has no scheduled change to reflect, and the new
      // active plan's price/name aren't known client-side, so skip the optimistic
      // write — the onSettled refetch supplies the authoritative active state.
      // The union guarantees a downgrade carries `effectiveAt`, so `isUpgrade`
      // alone discriminates the branch.
      if (variables.isUpgrade) {
        return;
      }

      const { targetProductId, effectiveAt } = variables;

      queryClient.setQueryData<GetBillingInfoResponse>(
        billingInfoKey,
        (prev) =>
          prev
            ? {
                ...prev,
                subscriptionState: SUBSCRIPTION_STATE.SCHEDULED_TO_CHANGE,
                scheduledChange: { targetProductId, effectiveAt },
                cancelAt: null,
              }
            : prev,
      );
    },
    onError: (error) => {
      captureBillingError(error, 'change-plan');
    },
    onSettled: (_data, _error, { isUpgrade }) => {
      queryClient.invalidateQueries({ queryKey: billingInfoKey });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.BILLING_ACCOUNT_INFO],
      });

      // An upgrade grants the larger pack's credits immediately, so the quota
      // balance must be refetched too. A downgrade is scheduled at period end —
      // credits don't change now, so its balance stays valid.
      if (isUpgrade) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.BILLING_QUOTA_BALANCE],
        });
      }
    },
  });
};
